<script>
import { adjustControlConditionalVisibility, adjustControlConditionalValues, filterUserConfigurableConfigs } from '@/utils/utils';
import DynamicField from './DynamicField.vue';
import TextField from './TextField.vue';
import { ChevronDown } from 'lucide-vue-next';
import { Card, CardContent } from '@/components/ui/card';
import _ from 'lodash';

export default {
    components: {
        TextField,
        DynamicField,
        ChevronDown,
        Card,
        CardContent
    },
    props: {
        connector: {
            type: Object,
            required: true
        },
        defaultConnector: {
            type: Object,
            required: true
        },
        config: {
            type: Object,
            required: true
        },
        tab: {
            type: String,
            required: true
        },
        editMode: {
            type: Boolean,
            required: true
        },
        justifyContent: {
            type: String,
            default: 'justify-center'
        },
        entityType: {
            type: String,
            required: true,
            default: ''
        }
    },
    data() {
        return {
            defaultConfig: _.cloneDeep(this.defaultConnector.config),
            defaultName: _.cloneDeep(this.defaultConnector.name),
            advancedMode: false,
            currentConfig: _.cloneDeep(this.config),
            isSmallScreen: false
        };
    },
    computed: {
        separateNameControlNeeded() {
            return this.entityType !== 'transform';
        },
        configExists() {
            return (!_.isEmpty(this.connector.config) && !_.isEmpty(this.currentConfig)) || this.connector.connector == 'kafkadirect';
        },
        advancedProps() {
            return this.currentConfig?.filter((config) => config.display_advanced).map((config) => config.name);
        },
        hideLateralDescription() {
            // Hide descriptions if not in edit mode OR if screen is small
            return !this.editMode || this.isSmallScreen;
        },
        visibleBasicConnectorConfigControls() {
            // Create a deep clone to avoid modifying the original currentConfig
            const filteredConfigs = _.cloneDeep(filterUserConfigurableConfigs(this.currentConfig));
            const adjustedControls = _.chain(this.adjustControlConditionalValues(filteredConfigs))
                .filter((x) => !x.display_advanced)
                .orderBy('order_of_display', 'asc')
                .map((control) => this.adjustControlConditionalVisibility(control, filteredConfigs)?.control || null)
                .compact()
                .value();

            let controlsToRecheck = adjustedControls.filter((c) => c.conditions?.length);

            if (controlsToRecheck.length) {
                controlsToRecheck.map((oldControl) => {
                    return this.onFieldChanged({
                        value: oldControl.value.current_value || oldControl.value.default,
                        valid: oldControl.value.valid || false,
                        name: oldControl.name
                    });
                });
            }

            return _.groupBy(adjustedControls, 'tab');
        },
        visibleAdvancedConnectorConfigControls() {
            // Create a deep clone to avoid modifying the original currentConfig
            const filteredConfigs = _.cloneDeep(filterUserConfigurableConfigs(this.currentConfig));
            const adjustedControls = _.chain(this.adjustControlConditionalValues(filteredConfigs))
                .filter((x) => x.display_advanced)
                .orderBy('order_of_display', 'asc')
                .map((control) => this.adjustControlConditionalVisibility(control, filteredConfigs)?.control || null)
                .compact()
                .value();

            let controlsToRecheck = adjustedControls.filter((c) => c.conditions?.length);

            if (controlsToRecheck.length) {
                controlsToRecheck.map((oldControl) => {
                    return this.onFieldChanged({
                        value: oldControl.value.current_value || oldControl.value.default,
                        valid: oldControl.value.valid || false,
                        name: oldControl.name
                    });
                });
            }

            return _.groupBy(adjustedControls, 'tab');
        }
    },
    watch: {
        config: {
            handler(val) {
                this.currentConfig = _.cloneDeep(val);
            },
            deep: true
        },
        'connector.config': {
            handler(val) {
                const keys = _.keys(val).filter((key) => !this.advancedProps.includes(key));

                let value = _.omit(
                    this.defaultConfig,
                    Object.keys(this.defaultConfig).filter((key) => !keys.includes(key) && !this.advancedProps.includes(key))
                );

                // we have to populate this with the default values if we have any from the general default config from BE
                value = _.mapValues(value, (v, key) => {
                    if (_.isNil(v)) {
                        const configVal = this.currentConfig.find((x) => x.name == key)?.value?.default;

                        // so we can include also the boolean values or 0 cases
                        return _.isBoolean(configVal) || _.isNumber(configVal) ? configVal : configVal || v;
                    }

                    return v;
                });

                const valueKeys = _.keys(value);
                const configKeys = _.map(this.currentConfig, 'name');

                // we have to populate this with the default values if we have any from the general default config from BE - that is not included at all in the connector config
                const diff = _.difference(configKeys, valueKeys);

                diff.forEach((key) => {
                    const configVal = this.currentConfig.find((x) => x.name == key)?.value?.default;

                    if (!_.isNil(configVal)) {
                        value[key] = configVal;
                    }
                });

                // Normalize both configs for accurate comparison (handle undefined → false for boolean fields)
                const normalizedVal = this.normalizeConfigForComparison(val);
                const normalizedValue = this.normalizeConfigForComparison(value);
                
                this.$emit('changed', !_.isEqual(normalizedVal, normalizedValue) || (this.separateNameControlNeeded ? false : this.connector.name !== this.defaultName));
            },
            deep: true
        }
    },
    mounted() {
        this.defaultConfig = _.cloneDeep(this.defaultConnector.config);
        this.defaultName = _.cloneDeep(this.defaultConnector.name);
        // Setup responsive handler
        this.handleResize();
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        adjustControlConditionalValues,
        adjustControlConditionalVisibility,
        handleResize() {
            this.isSmallScreen = window.innerWidth <= 1024;
        },
        // Normalize config objects for comparison by treating undefined as false for boolean values
        normalizeConfigForComparison(config) {
            return _.mapValues(config, (value, key) => {
                // Find the control definition to check if it's a boolean field
                const control = this.currentConfig.find(x => x.name === key);
                const isBoolean = control?.type === 'BOOLEAN' || _.isBoolean(value) || _.isBoolean(control?.value?.default);
                
                // If it's a boolean field and value is nil, normalize to false (matching onFieldChanged logic)
                if (isBoolean && _.isNil(value)) {
                    return false;
                }
                
                return value;
            });
        },
        onFieldChanged({ value, valid, name }) {
            const control = this.currentConfig.find((x) => x.name == name);
            let currentValue = control?.value?.current_value;

            if (_.isNil(currentValue) && _.isBoolean(value)) {
                currentValue = false;
            }

            if (_.isEqual(currentValue, value)) {
                return;
            }

            if (control) {
                control.value.current_value = value;
                control.value.valid = valid;

                // Update the filtered configs for visibility calculations
                const filteredConfigs = filterUserConfigurableConfigs(this.currentConfig);
                const updatedFilteredConfigs = _.chain(this.adjustControlConditionalValues(filteredConfigs))
                    .map((control) => this.adjustControlConditionalVisibility(control, filteredConfigs)?.control || null)
                    .compact()
                    .value();

                // Merge back with non-user-configurable configs to maintain the original structure
                const nonUserConfigs = this.currentConfig.filter((config) => config.global || !config.user_defined);
                this.currentConfig = [...updatedFilteredConfigs, ...nonUserConfigs];
            }

            if (valid && !_.isUndefined(value)) {
                if (this.connector) {
                    this.connector.config[name] = value;
                }
            }
        },
        handleNameChange(newName) {
            this.connector.name = newName;

            const keys = _.keys(this.connector.config).filter((key) => !this.advancedProps.includes(key));
            const value = _.omit(
                this.defaultConfig,
                Object.keys(this.defaultConfig).filter((key) => !keys.includes(key) && !this.advancedProps.includes(key))
            );

            this.$emit('changed', this.connector.name !== this.defaultName || !_.isEqual(this.connector.config, value));
        },
        getClass(control, advanced = false, tab = null) {
            const spaceLeft = control.space_left || 0;

            let usedConnectors = advanced ? this.visibleAdvancedConnectorConfigControls?.[tab] : this.visibleBasicConnectorConfigControls?.[tab];

            usedConnectors = _.orderBy(
                usedConnectors.filter((x) => x.display_in_ui),
                'order_of_display',
                ['asc']
            );

            const currentControlIndex = usedConnectors.findIndex((x) => x.name === control.name);
            const nextControl = usedConnectors[currentControlIndex + 1];

            const nextControlSpaceLeft = nextControl ? nextControl.space_left : undefined;

            // Predefined mapping for padding-left classes.
            const paddingLeftMapping = {
                0: 'pl-0',
                1: 'pl-5',
                2: 'pl-10',
                3: 'pl-15',
                4: 'pl-20',
                5: 'pl-25',
                6: 'pl-30'
            };

            const paddingLeftClass = paddingLeftMapping[spaceLeft] || '';

            // Determine margin based on the spacing of the next control.
            let marginBottomClass = 'mb-6';
            if (nextControlSpaceLeft === undefined) {
                marginBottomClass = 'mb-6'; // Last control: standard margin.
            } else if (nextControlSpaceLeft > spaceLeft) {
                marginBottomClass = 'mb-2'; // Child control: smaller margin.
            } else if (nextControlSpaceLeft === spaceLeft) {
                marginBottomClass = 'mb-6'; // Same level: standard margin.
            } else if (nextControlSpaceLeft < spaceLeft) {
                marginBottomClass = 'mb-8'; // Ending a group: larger margin.
            }

            return `${paddingLeftClass} ${marginBottomClass}`.trim();
        }
    }
};
</script>

<template>
    <Card v-if="configExists" class="w-full">
        <CardContent class="p-0">
            <div class="pr-2">
                <div class="px-6">
                    <form class="grid grid-cols-1 sm:grid-cols-1" action="#" method="POST" autocomplete="new-password">
                        <TextField
                            v-if="tab === 'settings' && separateNameControlNeeded"
                            :config="{
                                display_name: 'Connector name',
                                description: 'The name of the connector'
                            }"
                            :edit-mode="editMode"
                            :value="connector.name"
                            :override-label-opacity="true"
                            :hide-lateral-description="!editMode"
                            class="pb-6"
                            @input="handleNameChange" />
                        <div v-show="control.display_in_ui" v-for="control in visibleBasicConnectorConfigControls?.[tab]">
                            <DynamicField
                                :key="`${control.name}_${control.display_in_ui}`"
                                :config="control"
                                :value="connector.config[control.name]"
                                :edit-mode="editMode"
                                :input-class="getClass(control, false, tab)"
                                class="opacity-100!"
                                :override-label-opacity="true"
                                :hide-lateral-description="!editMode"
                                @input="onFieldChanged" />
                        </div>
                        <div v-if="tab !== 'settings' && !visibleBasicConnectorConfigControls?.[tab]">
                            <div class="flex justify-center items-center pb-5">
                                <div class="text-center">
                                    <h2 class="text-xl font-medium text-muted-foreground">No configuration available</h2>
                                </div>
                            </div>
                        </div>
                        <div v-if="(tab === 'settings' || tab === 'smt') && visibleAdvancedConnectorConfigControls?.[tab]?.length > 0">
                            <div
                                class="flex w-full items-center justify-between border px-4 py-3 font-medium transition-all hover:bg-accent cursor-pointer"
                                :class="advancedMode ? 'rounded-t-lg border-b-0' : 'rounded-lg'"
                                @click="advancedMode = !advancedMode">
                                Advanced
                                <ChevronDown class="h-4 w-4 shrink-0 transition-transform duration-200" :class="advancedMode ? 'rotate-180' : ''" />
                            </div>
                            <div v-show="advancedMode" class="border border-t-0 rounded-b-lg p-5">
                                <div class="grid grid-cols-1 sm:grid-cols-1 sm:gap-x-8">
                                    <div v-show="control.display_in_ui" v-for="control in visibleAdvancedConnectorConfigControls?.[tab]">
                                        <DynamicField
                                            :key="`${control.name}_${control.display_in_ui}`"
                                            :config="control"
                                            :value="connector.config[control.name]"
                                            :input-class="getClass(control, true, tab)"
                                            :hide-lateral-description="!editMode"
                                            :edit-mode="editMode"
                                            :override-label-opacity="true"
                                            @input="onFieldChanged" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </CardContent>
    </Card>
</template>

<style scoped></style>
